import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '@lombok/api-client';
import { Button } from '@lombok/ui';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** What will happen, in plain words. */
  children: ReactNode;
  confirmLabel: string;
  /**
   * Runs when the user confirms. If it throws, the message is shown inside the dialog. A toast
   * would sit under the modal, so the mutation behind it must set `meta: { inlineError: true }`.
   */
  onConfirm: () => Promise<unknown>;
  onClose: () => void;
  danger?: boolean;
  /** Keeps the confirm button off until the dialog's own inputs are valid (e.g. a required reason). */
  confirmDisabled?: boolean;
}

/**
 * Confirmation before a destructive or hard-to-undo action (delete, archive). Built on the
 * native <dialog>, so focus is trapped and Escape closes it. A refusal from the API (for
 * example "tiene historial") is shown inside the dialog, next to the button that triggered it.
 */
export function ConfirmDialog({ open, title, children, confirmLabel, onConfirm, onClose, danger, confirmDisabled }: ConfirmDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setError(undefined);
      dialog.showModal();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const confirm = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo completar la acción. Inténtalo de nuevo.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      // Escape must not close it while the request runs: the server's refusal is shown in here.
      onCancel={(e) => {
        if (busy) e.preventDefault();
      }}
      className="w-[calc(100%-2rem)] max-w-md rounded-lg border border-border bg-surface p-0 text-text-primary shadow-lg backdrop:bg-black/40"
    >
      <div className="flex flex-col gap-4 p-5">
        <h2 className="text-base font-semibold">{title}</h2>
        <div className="text-sm text-text-muted">{children}</div>
        {error ? (
          <p className="rounded-md border border-danger bg-danger/10 px-3 py-2 text-sm text-danger" role="alert">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} size="sm" onClick={() => void confirm()} disabled={busy || confirmDisabled}>
            {busy ? 'Procesando…' : confirmLabel}
          </Button>
        </div>
      </div>
    </dialog>
  );
}
